import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useContext,
} from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import { useLocation, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import Navbar from "../../components/navbar/Navbar";
import Menubar from "../../components/menubar/Menubar";
import StudyDetails from "../studyDetails/StudyDetails";
import WetReads from "../wetReads/WetReads";
import CriticalFinding from "../criticalFinding/CriticalFinding";
import AttachedDocument from "../attachedDocument/AttachedDocument";
import Chat from "../chat/Chat";
import PacsShareTab from "../chat/PacsShareTab";
import ProtocolPanel from "./ProtocolPanel";
import AcrDecisionSupport from "../acrDecisionSupport/AcrDecisionSupport";
import ChatErrorBoundary from "../../components/chat/ChatErrorBoundary";
import VideoRecording from "../videoRecording/VideoRecording";
import MDTRecording from "../mdtRecording/MDTRecording";
import Footer from "../../components/footer/Footer";
import Modal from "./Model";
import PreviewModal from "./PreviewModel";

import ReportEditorSection from "./components/ReportEditorSection";
import SidebarSection from "./components/SidebarSection";

import { insertHtmlIntoReportEditor } from "../../utils/insertHtmlIntoReportEditor";
import { useChatConnection } from "../../Hooks/useChatConnection";
import { useStudyById } from "../../utils/API_SERVICE_FOR_PATIENT_PAGE";
import {
  processWithAi,
  getSingleTemplate,
  useClientHeader,
  useClientFooter,
  createReports,
} from "../../utils/API_SERVICE";
import { StudyContext } from "../../context/studyData/studyData";
import useTemplateProcessor from "./hooks/useTemplateProcessor";
import useRteWithSpeech from "./hooks/useRteWithSpeech";
import usePatientModals from "./hooks/usePatientModals";
import usePatientReportDraft from "./hooks/usePatientReportDraft";
import usePhilipsDeviceConnector from "./hooks/usePhilipsDeviceConnector";
import usePhilipsSpeechMikeCommands from "./hooks/usePhilipsSpeechMikeCommands";
import { useMicFocusOrchestrator } from "./hooks/useMicFocusOrchestrator";
import { useStudyId } from "../../Hooks/useStudyId";
import { useDictationLanguage } from "../../Hooks/useLanguagePersistence";
import { useProcessAiModelSpeed } from "../../Hooks/useProcessAiModelSpeed";
import { useTranslation } from "react-i18next";
import { useI18nLanguage } from "../../i18n/hooks/useI18nLanguage";
import { useCatalog } from "../../api/hooks/useCatalog";
import {
  getSaveContentFromReport,
  getPrintContentFromReport,
  getReportHtmlSource,
  stripEmptyParagraphsFromHtml,
} from "../../utils/contentProcessor";
import {
  getReportClipboardPayload,
  writeReportToClipboard,
} from "../../utils/reportClipboard";
import {
  useCreReportHtmlSync,
  useRadicalSaveReportMessage,
  useRadicalPeerReviewMessage,
} from "./hooks/cre";
import { diffHtml } from "../../utils/htmlDiff";
import { usePatientAuthFlags } from "../../Hooks/usePatientAuthFlags";
import { useCrePermission } from "../../Hooks/useCrePermission";
import {
  CRE_PERMISSION,
  getCrePermissionDeniedMessage,
  resolveCreFeatureNotAllowedMessage,
} from "../../cre/crePermissions";
import { useGlobalStore } from "../../context/UserContext/userContext";
import {
  appendCreOrganisationToAiPayload,
  getCreOrganisationLabel,
  getCreOrganisations,
} from "../../cre/creOrganisation";
import {
  findNoTemplateTemplate,
  resolveNoTemplatePromptTypeName,
  isHebrewReportLanguage,
} from "../../cre/noTemplate";
import { resolveRelatedUserId } from "../../utils/relatedUserId";
import { resolveTemplateInstructionsOnSelect } from "../../utils/templateInstructions";
import { useFormulaCalculations } from "./hooks/useFormulaCalculations";
import { resolveFormulaCalcMarkers } from "../../utils/formulaCalculationMarkers";
import { getFormulaCatalog } from "../../api/formulaEngineApi";
import { useCreAgentMode } from "../../cre/agentMode/useCreAgentMode";
import { useCreAgentBridge } from "../../cre/agentMode/useCreAgentBridge";
import { useQueryClient } from "@tanstack/react-query";
import {
  ANATHOMY_UNKNOWN,
  resolveProcessAiPromptFile,
  resolveTemplateAnathomy,
} from "../../cre/templateAnathomy";

function Patient() {
  const { studyData, setStudyData: setGlobalStudyData } = useContext(
    StudyContext,
  );
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    isCreAuth,
    isRadicalAuth,
    creUser,
    radicalUser,
  } = usePatientAuthFlags();
  const { requirePermission } = useCrePermission();
  // CRE Agent Mode (Windows agent, ?agent=1): editor only. Off for every normal session.
  const { agentMode } = useCreAgentMode();
  const queryClient = useQueryClient();
  const creSelectedOrganisationId = useGlobalStore(
    (s) => s.creSelectedOrganisationId,
  );
  const initialRowData = location.state?.rowData;
  const propStudyId = searchParams.get("StudyId");
  const studyId = useStudyId(propStudyId);
  const rteRef = useRef();
  const editorRef = useRef();
  const [sections, setSections] = useState(["rte", "sidebar"]);
  const [isMobileView, setIsMobileView] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState("");
  const [isSavingReport, setIsSavingReport] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [sidebarTranscript, setSidebarTranscript] = useState("");
  // Use i18n language hook - syncs with react-i18next and localStorage
  const { language, changeLanguage } = useI18nLanguage(); // Language for UI/RTL support - synced with i18n
  const [dictationLanguage, setDictationLanguage] = useDictationLanguage(); // Dictation language for voice commands
  const [modelSpeed, setModelSpeed] = useProcessAiModelSpeed();
  const [findingsOnly, setFindingsOnly] = useState(false);
  const { t } = useTranslation(["common", "protocol"]);

  const [templateInstructions, setTemplateInstructions] = useState("");
  /** Saved instructions the current template put in the field (swapped out on template change). */
  const lastTemplateInstructionsRef = useRef("");
  // Single state containing full anatomy object with all properties (id, code, name, promptFile, etc.)
  const [selectedAnathomy, setSelectedAnathomy] = useState(null);
  const [anathomies, setAnathomies] = useState([]); // Store full anatomies array to pass to PatientSidebar
  // Macros list for voice commands ("Short <name>" / "Insert <name>" / "קיצור <name>")
  const [macrosForVoice, setMacrosForVoice] = useState([]);
  // Templates list for voice commands ("Template <name>")
  const [templatesForVoice, setTemplatesForVoice] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  /** CRE-only: Process AI via global backend "No Template" (does not clear dictation or selectedTemplateId). */
  const [noTemplateMode, setNoTemplateMode] = useState(false);
  const {
    queue: formulaQueue,
    enqueueFormula,
    clearQueue: clearFormulaQueue,
    getCalculationRequests,
    manualDialog: formulaManualDialog,
    openManualDialog: openFormulaManualDialog,
    closeManualDialog: closeFormulaManualDialog,
    beginFollowUps: beginFormulaFollowUps,
  } = useFormulaCalculations();

  // Backward compatibility: derive anatomy code string from selectedAnathomy
  const anathomy = selectedAnathomy?.code || "";

  const { getAnatomies } = useCatalog();
  const { data: firstStudyData } = useStudyById(studyId);
  const { data: clientHeaderData } = useClientHeader({});
  const { data: clientFooterData } = useClientFooter({});

  const relatedUserId = useMemo(
    () =>
      resolveRelatedUserId({
        search: location.search,
        creUserId: creUser?.userId,
        radicalUserId: radicalUser?.userId,
        studyUserId: firstStudyData?.userId,
      }),
    [
      location.search,
      creUser?.userId,
      radicalUser?.userId,
      firstStudyData?.userId,
    ],
  );

  // Must match API user resolution; embed often passes RelatedUserId in the URL.
  const draftUserId = relatedUserId;

  const handleVoiceMacroResult = useCallback(
    (result) => {
      if (!result) return;
      if (result.success) {
        toast.success(
          t("macroInserted", { macroName: result.macro?.macroName ?? "" }),
        );
      } else {
        const name = result.macroName ?? "";
        const msg = result.multiple
          ? t("voiceMacroMultipleMatches", { macroName: name })
          : result.emptyContent
            ? `Macro "${name}" has no content to insert`
            : t("voiceMacroNotFound", { macroName: name });
        toast.warning(msg);
      }
    },
    [t],
  );

  const handleTemplateSelectRef = useRef(() => {});

  /** Editor (RTE) mic only — drives `useRteWithSpeech` command / dictation behaviour. */
  const [isCommandMode, setIsCommandMode] = useState(false);

  const {
    transcript,
    setTranscript,
    isMicActive,
    handleMicClick,
    startMic,
    stopMic,
    addRecordedTranscript,
    resetTranscriptState,
    streamingTranscript,
    selectNextParagraph,
    selectPreviousParagraph,
    selectContentUnderHeader,
  } = useRteWithSpeech({
    editorRef,
    initialContent: "",
    language: dictationLanguage, // Use dictation language for voice commands
    anathomy: anathomy, // Pass anathomy type
    macros: macrosForVoice,
    onVoiceMacroResult: handleVoiceMacroResult,
    commandMode: isCommandMode,
  });

  // CRE PiP “Copy report” reads `data-cre-report-html` on the editor root (see useCreReportHtmlSync).
  useCreReportHtmlSync(editorRef, transcript);

  const sidebarStopMicRef = useRef(null);
  const sidebarStartMicRef = useRef(null);
  const sidebarClearReportRef = useRef(null);
  const pendingClearTranscriptionRef = useRef(null);
  const sidebarTextareaRef = useRef(null);
  const [isSidebarRecording, setIsSidebarRecording] = useState(false);

  const { removeDraft } = usePatientReportDraft({
    // Not in Agent Mode: the Windows agent owns the patient session (its
    // dictation + template). A restored draft would show a template/report the
    // agent never chose and race with the agent's own Process AI.
    enabled: !!studyId && !!draftUserId && !agentMode,
    userId: draftUserId,
    studyId,
    transcriptHtml: transcript,
    sidebarTranscript,
    selectedTemplateId,
    templateInstructions,
    onRestore: (draft) => {
      if (!draft) return;
      if (draft.editorHtml != null) setTranscript(String(draft.editorHtml));
      if (draft.sidebarTranscript != null)
        setSidebarTranscript(String(draft.sidebarTranscript));
      if (draft.selectedTemplateId !== undefined) {
        const nextTemplateId = draft.selectedTemplateId ?? null;
        setSelectedTemplateId(nextTemplateId);
      }
      if (draft.templateInstructions !== undefined) {
        setTemplateInstructions(
          isCreAuth
            ? String(draft.templateInstructions || "")
            : "",
        );
      }
    },
  });

  const isMicActiveRef = useRef(isMicActive);
  isMicActiveRef.current = isMicActive;

  const startEditorMicForFocus = useCallback(async () => {
    await startMic();
  }, [startMic]);

  const startSidebarMic = useCallback(async () => {
    if (isMicActiveRef.current) {
      stopMic();
    }
    await sidebarStartMicRef.current?.();
  }, [stopMic]);

  const stopSidebarMic = useCallback(async () => {
    if (sidebarStopMicRef.current) {
      await sidebarStopMicRef.current();
    }
  }, []);

  const {
    handleSidebarFocus,
    handleEditorFocus,
    micToggleTarget,
    isAnyMicActive,
  } = useMicFocusOrchestrator({
    isEditorMicActive: isMicActive,
    isSidebarRecording,
    stopEditorMic: stopMic,
    startEditorMic: startEditorMicForFocus,
    stopSidebarMic,
    startSidebarMic,
  });

  const handleEditorMicClick = useCallback(async () => {
    setIsCommandMode(false);
    if (isAnyMicActive) {
      stopMic();
      await stopSidebarMic();
      return;
    }
    if (!requirePermission(CRE_PERMISSION.BASIC)) {
      return;
    }
    if (sidebarStopMicRef.current) {
      await sidebarStopMicRef.current();
    }
    await handleMicClick();
  }, [
    isAnyMicActive,
    stopMic,
    stopSidebarMic,
    handleMicClick,
    requirePermission,
  ]);

  const handleSidebarMicClick = useCallback(async () => {
    if (isAnyMicActive) {
      stopMic();
      await stopSidebarMic();
      return;
    }
    if (!requirePermission(CRE_PERMISSION.BASIC)) {
      return;
    }

    // Study dictation always starts in the Findings dictation box (not last editor focus).
    await startSidebarMic();
    queueMicrotask(() => {
      sidebarTextareaRef.current?.focus?.();
    });
  }, [
    isAnyMicActive,
    requirePermission,
    stopMic,
    stopSidebarMic,
    startSidebarMic,
  ]);

  const handleBeforeSidebarMicStart = useCallback(async () => {
    if (isMicActive) {
      stopMic();
    }
  }, [isMicActive, stopMic]);

  const registerSidebarStopMicHandler = useCallback((handler) => {
    sidebarStopMicRef.current = handler;
  }, []);

  const registerSidebarStartMicHandler = useCallback((handler) => {
    sidebarStartMicRef.current = handler;
  }, []);

  const registerSidebarClearReportHandler = useCallback((handler) => {
    sidebarClearReportRef.current = handler;
  }, []);

  // Fetch anatomies and store full objects with all properties
  useEffect(() => {
    const fetchAnatomies = async () => {
      try {
        const anathomyCatalog = await getAnatomies();
        if (anathomyCatalog) {
          // Store full anatomies array with all properties (id, code, name, promptFile, etc.)
          const items = anathomyCatalog.catalogItems.map((item) => ({
            id: item.id,
            code: item.code,
            name: item.name,
            promptFile: item.promptFile,
          }));
          setAnathomies(items);
        }
      } catch (error) {
        console.error("Error fetching anatomies:", error);
      }
    };
    fetchAnatomies();
  }, [getAnatomies]);

  // Update selectedAnathomy when anatomies are loaded and we have a code but incomplete object
  useEffect(() => {
    if (!selectedAnathomy?.code || anathomies.length === 0) return;

    // Only update if the current selectedAnathomy doesn't have a name (incomplete object)
    if (!selectedAnathomy.name) {
      const foundAnathomy = anathomies.find(
        (item) => item.code === selectedAnathomy.code,
      );
      if (foundAnathomy) {
        setSelectedAnathomy(foundAnathomy);
      }
    }
  }, [anathomies, selectedAnathomy]);

  useEffect(() => {
    console.log("Patient component initializing with data:", {
      hasInitialRowData: !!initialRowData,
      initialContentLength: initialRowData?.content?.length || 0,
      hasStudyData: !!firstStudyData,
      editorReady: !!editorRef.current?.editor,
    });
    // Radical users: no template instructions field — keep empty
    const initialInstructions = isCreAuth
      ? initialRowData?.templateInstructions || ""
      : "";
    lastTemplateInstructionsRef.current = initialInstructions;
    setTemplateInstructions(initialInstructions);
  }, [initialRowData, firstStudyData, editorRef, isCreAuth]);

  const {
    processedHeader,
    processedFooter,
    processText,
  } = useTemplateProcessor(
    studyData, // Use dynamic data from context so it reflects latest changes
    clientHeaderData?.content,
    clientFooterData?.content,
  );

  const {
    isModalVisible,
    modalMessage,
    actionType,
    showModal,
    hideModal,
    isPreviewVisible,
    showPreview,
    hidePreview,
  } = usePatientModals();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const orderedSections =
    agentMode || isMobileView ? ["sidebar", "rte"] : sections;

  useEffect(() => {
    if (firstStudyData) {
      setGlobalStudyData(firstStudyData);
    }
  }, [firstStudyData, setGlobalStudyData]);

  const chatConnection = useChatConnection({
    studyId,
    accessionId: firstStudyData?.accessionNo,
    accessionNo: firstStudyData?.accessionNo,
    patientWhatsAppNumber:
      // firstStudyData?.patientPhone ||
      // firstStudyData?.phone ||
      "+923178350140",
    isOpen:
      selectedComponent === "Chats" || selectedComponent === "PACSShare",
  });

  const { markAsRead: chatMarkAsRead } = chatConnection;
  const handleSelectNewSection = useCallback(
    (section) => {
      setSelectedComponent(section);
      if (section === "Chats") chatMarkAsRead();
    },
    [chatMarkAsRead],
  );

  const handleTranscriptChange = useCallback(
    (newTranscript) => {
      setTranscript(newTranscript);
    },
    [setTranscript],
  );

  const handleInsertIntoReport = useCallback(
    (content) => {
      const editor = editorRef.current?.editor;
      if (!editor) {
        toast.error(
          t("mdt.insertNotAvailable") || "Insert into report is not available.",
        );
        return;
      }

      const contentStr =
        typeof content === "string" ? content : String(content || "");
      if (!contentStr.trim()) {
        toast.error(t("mdt.nothingToInsert") || "Nothing to insert.");
        return;
      }

      const result = insertHtmlIntoReportEditor({
        editor,
        html: contentStr,
        setTranscript,
        onError: () => {
          toast.error(
            t("mdt.insertFailed") || "Failed to insert content into report.",
          );
        },
      });

      if (!result.ok && result.reason === "empty") {
        toast.error(t("mdt.nothingToInsert") || "Nothing to insert.");
      }
    },
    [setTranscript, t],
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1023px)");
    const handleChange = (event) => {
      setIsMobileView(event.matches);
    };

    handleChange(mediaQuery);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      if (isMobileView) return;
      const { active, over } = event;
      if (active.id !== over.id) {
        setSections((prevSections) => {
          const oldIndex = prevSections.indexOf(active.id);
          const newIndex = prevSections.indexOf(over.id);
          return arrayMove(prevSections, oldIndex, newIndex);
        });
      }
    },
    [isMobileView],
  );

  const formatResponse = useCallback((text) => {
    if (typeof text !== "string") {
      console.error("Error: formatResponse expects a string input.");
      return "";
    }

    const lines = text.split("\n").map((line) => line.trim());

    let htmlOutput = "";
    let inList = null; // null, 'ul', or 'ol'
    let currentListHtml = "";

    const closeListIfNeeded = () => {
      if (inList && currentListHtml) {
        htmlOutput += `<${inList}>${currentListHtml}</${inList}>`;
      }
      inList = null;
      currentListHtml = "";
    };

    lines.forEach((line) => {
      if (!line) return; // Skip empty lines entirely

      // 1. Section Headers (---**HEADER**---)
      const sectionHeaderMatch = line.match(/^---?\*\*(.+?)\*\*---?$/);
      if (sectionHeaderMatch) {
        closeListIfNeeded();
        const headerText = sectionHeaderMatch[1].trim().toUpperCase();
        const level = [
          "PATIENT INFORMATION",
          "EXAM",
          "CLINICAL HISTORY",
          "TECHNIQUE",
          "CONTRAST",
          "COMPARISON",
          "FINDINGS",
          "IMPRESSION",
        ].includes(headerText)
          ? 2
          : 3;
        htmlOutput += `<h${level}>${headerText}</h${level}>`;
        return;
      }

      // 2. Image (![Alt](Src))
      const imageMatch = line.match(/^!\[(.*?)\]\((.*?)\)/);
      if (imageMatch) {
        closeListIfNeeded();
        htmlOutput += `<p><img src="${imageMatch[2]}" alt="${imageMatch[1]}"></p>`;
        return;
      }

      // 3. Impression List Items (1. Text)
      const impressionItemMatch = line.match(/^(\d+)\.\s+(.*)/);
      if (impressionItemMatch) {
        if (inList !== "ol") {
          closeListIfNeeded();
          inList = "ol";
        }
        currentListHtml += `<li>${impressionItemMatch[2].trim()}</li>`;
        return;
      }

      // 4. Findings/Patient Info List Items (- Text or - **Key:** Value)
      if (line.startsWith("- ")) {
        if (inList !== "ul") {
          closeListIfNeeded();
          inList = "ul";
        }
        const itemContent = line.substring(2).trim();
        // Check if it's a key-value within the list item
        const kvMatch = itemContent.match(/^\*\*(.+?)\*\*:\s*(.*)/);
        if (kvMatch) {
          currentListHtml += `<li><strong>${kvMatch[1].trim()}:</strong> ${kvMatch[2].trim()}</li>`;
        } else {
          currentListHtml += `<li>${itemContent}</li>`;
        }
        return;
      }

      // 5. Simple Headers (**HEADER:** Text)
      const simpleHeaderMatch = line.match(/^\*\*(.+?)\*\*:\s*(.*)/);
      if (simpleHeaderMatch) {
        closeListIfNeeded();
        // Check if it's just a sub-header like BRAIN:
        if (
          !simpleHeaderMatch[2].trim() ||
          simpleHeaderMatch[2].trim().startsWith("-")
        ) {
          htmlOutput += `<p><strong>${simpleHeaderMatch[1].trim()}:</strong></p>`;
        } else {
          htmlOutput += `<p><strong>${simpleHeaderMatch[1].trim()}:</strong> ${simpleHeaderMatch[2].trim()}</p>`;
        }
        return;
      }

      // 6. Italicized text (*Text*)
      if (line.startsWith("*") && line.endsWith("*") && !line.includes("**")) {
        closeListIfNeeded();
        htmlOutput += `<p><em>${line.slice(1, -1)}</em></p>`;
        return;
      }

      // 7. Default to Paragraph
      closeListIfNeeded();
      // Avoid adding specific non-content lines as paragraphs
      if (
        !line.match(/^Support Email:/) &&
        !line.match(/^Support Phone:/) &&
        !line.match(/^Contact us at/)
      ) {
        htmlOutput += `<p>${line}</p>`;
      }
    });

    // Final cleanup: close any remaining open list
    closeListIfNeeded();

    return htmlOutput;
  }, []);

  const handleProcessWithAI = useCallback(async () => {
    if (!requirePermission(CRE_PERMISSION.PROCESS_AI)) {
      return {
        ok: false,
        mirrorLevel: "error",
        mirrorMessage: getCrePermissionDeniedMessage(),
      };
    }
    // Auto-stop any active dictation (editor mic or sidebar mic) before
    // sending to the AI — otherwise the speech socket keeps streaming
    // while the AI generates the report.
    try {
      if (isMicActive) {
        stopMic();
      }
      if (sidebarStopMicRef.current) {
        await sidebarStopMicRef.current();
      }
    } catch (e) {
      console.warn("Could not stop active mic before Process AI:", e);
    }

    // Check if user has added findings / transcription content
    if (!sidebarTranscript || sidebarTranscript.trim() === "") {
      toast.error(t("pleaseAddFindingsBeforeAI"));
      return {
        ok: false,
        mirrorLevel: "error",
        mirrorMessage: t("pleaseAddFindingsBeforeAI"),
      };
    }

    setIsAiLoading(true);
    try {
      let templateContent =
        initialRowData?.content || "No Template content available";
      let instructions =
        isCreAuth ? templateInstructions || "" : "";
      // From the selected template itself, not the anatomy state: that state
      // follows the template one render later (agent: select + Process AI in
      // one step would otherwise send the previous template's prompt).
      let promptFile = resolveProcessAiPromptFile({
        templateId: selectedTemplateId,
        templates: templatesForVoice,
        anathomies,
        selectedAnathomy,
      });
      let processTemplateId = selectedTemplateId || null;
      // In No Template mode, never send the live editor (may hold a normal
      // template skeleton). Use the global No Template body instead.
      let userInputContent =
        getSaveContentFromReport({
          editor: editorRef.current?.editor,
          transcript,
        }) || "No User content available";

      if (isCreAuth && noTemplateMode && isHebrewReportLanguage(language)) {
        const noTpl = findNoTemplateTemplate(templatesForVoice);
        if (!noTpl?.templateId) {
          const msg = t("noTemplateModeUnavailable", {
            defaultValue:
              "No Template is not configured for your environment. Please contact your administrator.",
          });
          toast.error(msg);
          return {
            ok: false,
            mirrorLevel: "error",
            mirrorMessage: msg,
          };
        }

        let noTplContent = String(noTpl.content ?? noTpl.Content ?? "");
        let noTplInstructions = String(
          noTpl.templateInstructions ?? noTpl.TemplateInstructions ?? "",
        );
        if (!noTplContent.trim()) {
          try {
            const fetched = await getSingleTemplate({
              templateId: noTpl.templateId,
            });
            const payload = fetched?.payload;
            if (payload) {
              noTplContent = String(payload.content ?? noTplContent);
              noTplInstructions = String(
                payload.templateInstructions ?? noTplInstructions,
              );
            }
          } catch (fetchErr) {
            console.warn("Failed to fetch No Template content:", fetchErr);
          }
        }

        if (!noTplContent.trim()) {
          const msg = t("noTemplateModeUnavailable", {
            defaultValue:
              "No Template is not configured for your environment. Please contact your administrator.",
          });
          toast.error(msg);
          return {
            ok: false,
            mirrorLevel: "error",
            mirrorMessage: msg,
          };
        }

        templateContent = noTplContent;
        instructions = noTplInstructions;
        // Prefer backend promptFile; CRE_UNIVERSAL_NO_TEMPLATE_PROMPT_v1.txt only if missing.
        promptFile = resolveNoTemplatePromptTypeName(noTpl, anathomies);
        processTemplateId = noTpl.templateId;
        // Structure headers only — not the radiologist's previously selected template.
        userInputContent = templateContent;
      }

      const Aiobj = {
        StudyId: studyId,
        RelatedUserId: relatedUserId,
        TemplateContent: templateContent,
        UserInputContent: userInputContent,
        TemplateInstructions: instructions,
        FindingsText: sidebarTranscript || "",
        Language: language || "en",
        PromptTypeName: promptFile,
        ModelSpeed: modelSpeed,
      };

      if (processTemplateId) {
        Aiobj.templateId = processTemplateId;
      }

      if (!isCreAuth && findingsOnly) {
        Aiobj.Restrictions = { FindingsOnly: true };
      }

      const calculationRequests =
        isCreAuth && formulaQueue.length > 0 ? getCalculationRequests() : [];
      if (calculationRequests.length > 0) {
        Aiobj.calculationRequests = calculationRequests;
      }

      let aiPayload = Aiobj;
      if (isCreAuth) {
        if (!creSelectedOrganisationId) {
          toast.error(
            "Please select an organisation before using Process with AI.",
          );
          return {
            ok: false,
            mirrorLevel: "error",
            mirrorMessage: "Please select an organisation.",
          };
        }
        aiPayload = appendCreOrganisationToAiPayload(
          Aiobj,
          creSelectedOrganisationId,
        );
      }

      const response = await processWithAi(aiPayload);
      if (response) {
        const responseText =
          typeof response === "string"
            ? response
            : response?.payload?.content || ""; // Get raw text, ensure string

        const calculations = Array.isArray(response?.payload?.calculations)
          ? response.payload.calculations
          : [];

        // Resolve {CALC:id} markers before format/diff/setContent (Vigen contract).
        const markerPass = resolveFormulaCalcMarkers(
          responseText,
          calculations,
        );
        const contentAfterMarkers = markerPass.html;

        if (response?.payload?.calculationExtractionFailed) {
          toast.warning(
            t("formulaEngine.extractionFailed", {
              defaultValue:
                "Automatic formula extraction failed. Use ƒ → Manual to enter values.",
            }),
          );
        }

        const isAlreadyHtml = /<h[1-6]|<p>|<ol>|<ul>|<li>|<div>/i.test(
          contentAfterMarkers,
        );
        const cleanedText = isAlreadyHtml
          ? contentAfterMarkers
          : contentAfterMarkers
              .replace(/<p><\/p>/g, "\n")
              .replace(/<p>/g, "\n")
              .replace(/<\/p>/g, "\n")
              .trim();

        const formattedText = stripEmptyParagraphsFromHtml(
          isAlreadyHtml ? cleanedText : formatResponse(cleanedText),
        );

        // "Before" for diff: current editor HTML (or transcript)
        const originalHtml = getReportHtmlSource({
          editor: editorRef.current?.editor,
          transcript,
        });

        // Run DOM-based diff to mark added/modified blocks
        const highlightedHtml = diffHtml(originalHtml, formattedText);

        const processedText = highlightedHtml;

        setTranscript(processedText);

        // Apply highlighted content directly so diff classes appear immediately
        try {
          editorRef.current?.editor?.commands?.setContent(processedText, false);
        } catch (_) {
          // If setContent fails, state update still applies
        }

        // Post-apply: needs_input / blocked → sequential Manual dialogs (CRE).
        // Pending follow-ups are independent of calculationRequests queue.
        if (isCreAuth && (markerPass.needsInput.length || markerPass.blocked.length)) {
          const followUpsRaw = [
            ...markerPass.blocked.map((c) => ({ ...c, _kind: "blocked" })),
            ...markerPass.needsInput.map((c) => ({
              ...c,
              _kind: "needs_input",
            })),
          ];

          const built = [];
          let catalogCache = null;
          const resolveFormulaDef = async (item) => {
            const queuedMatch = formulaQueue.find(
              (q) =>
                String(q.calculationId) === String(item.calculationId),
            );
            if (queuedMatch?.formula) return queuedMatch.formula;
            if (!item.formulaId) return null;
            if (!catalogCache) {
              try {
                catalogCache = await getFormulaCatalog();
              } catch (_) {
                catalogCache = [];
              }
            }
            const fromCatalog =
              catalogCache.find(
                (f) => String(f.formulaId) === String(item.formulaId),
              ) || null;
            if (fromCatalog) return fromCatalog;
            return {
              formulaId: item.formulaId,
              version: item.formulaVersion || "1.0.0",
              nameEn: item.formulaId,
              parameters: [],
            };
          };

          for (const item of followUpsRaw) {
            const formulaDef = await resolveFormulaDef(item);
            if (!formulaDef) continue;

            if (item._kind === "blocked") {
              toast.warning(
                item.message ||
                  t("formulaEngine.blocked", {
                    defaultValue:
                      "Calculation blocked — confirmation required.",
                  }),
              );
            }

            // Capture live caret when opening (not doc end). Chip replace
            // prefers data-formula-pending; otherwise Manual inserts at cursor.
            let insertPos = null;
            try {
              const from = editorRef.current?.editor?.state?.selection?.from;
              insertPos = Number.isFinite(from) ? from : null;
            } catch (_) {
              insertPos = null;
            }

            built.push({
              formula: formulaDef,
              calculationId: item.calculationId,
              missingParameters: item.missingParameters,
              message: item.message,
              code: item.code,
              _kind: item._kind,
              insertPos,
            });
          }

          if (built.length > 0) {
            beginFormulaFollowUps(built);
          }
        }

        // Queue was consumed by this successful Process AI call only.
        // Does not clear pending Manual follow-ups.
        if (calculationRequests.length > 0) {
          clearFormulaQueue();
        }

        // Template Instructions are per run: clear after a successful Process AI.
        if (isCreAuth) {
          setTemplateInstructions("");
          lastTemplateInstructionsRef.current = "";
        }

        toast.success(t("aiProcessingComplete"));
        return {
          ok: true,
          mirrorLevel: "success",
          mirrorMessage: t("aiProcessingComplete"),
        };
      }
      toast.error(t("aiProcessingFailed"));
      return {
        ok: false,
        mirrorLevel: "error",
        mirrorMessage: t("aiProcessingFailed"),
      };
    } catch (error) {
      console.error("Error generating AI response:", error);
      const denied = resolveCreFeatureNotAllowedMessage(error);
      if (denied) {
        toast.info(denied);
        return {
          ok: false,
          mirrorLevel: "error",
          mirrorMessage: denied,
        };
      }
      toast.error(t("aiProcessingFailed"));
      return {
        ok: false,
        mirrorLevel: "error",
        mirrorMessage: t("aiProcessingFailed"),
      };
    } finally {
      setIsAiLoading(false);
    }
  }, [
    studyId,
    relatedUserId,
    initialRowData,
    transcript,
    setTranscript,
    sidebarTranscript,
    formatResponse,
    templateInstructions,
    isCreAuth,
    creSelectedOrganisationId,
    language,
    selectedAnathomy,
    modelSpeed,
    isMicActive,
    stopMic,
    selectedTemplateId,
    templatesForVoice,
    anathomies,
    noTemplateMode,
    t,
    findingsOnly,
    requirePermission,
    formulaQueue,
    getCalculationRequests,
    clearFormulaQueue,
    beginFormulaFollowUps,
  ]);

  const handlePrint = useCallback(() => {
    const printModalContent = `
      <html>
        <head>
          <title>${t("printContent")}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              hyphens: none;
              -webkit-hyphens: none;
              word-break: normal;
              overflow-wrap: normal;
            }
            body, body * {
              hyphens: none !important;
              -webkit-hyphens: none !important;
              word-break: normal !important;
              overflow-wrap: normal !important;
            }
            .header, .footer {
              margin-bottom: 20px;
            }
            .body-content {
              margin-bottom: 20px;
            }
            .flex {
              display: flex;
            }
            .justify-between {
              justify-content: space-between;
            }
            .items-center {
              align-items: center;
            }
            .mb-4 {
              margin-bottom: 1rem;
            }
            .h-8 {
              height: 2rem;
            }
            .text-right {
              text-align: right;
            }
            .text-sm {
              font-size: 0.875rem;
            }
            .text-blue-600 {
              color: #2563eb;
            }
            .hover\\:underline:hover {
              text-decoration: underline;
            }
            .text-center {
              text-align: center;
            }
            .text-2xl {
              font-size: 1.5rem;
            }
            .font-bold {
              font-weight: bold;
            }
            .my-4 {
              margin: 1rem 0;
            }
            .gap-4 {
              gap: 1rem;
            }
            .border-t-2 {
              border-top-width: 2px;
            }
            .border-gray-300 {
              border-color: #d1d5db;
            }
            .w-24 {
              width: 6rem;
            }
            .bg-blue-50 {
              background-color: #eff6ff;
            }
            .p-4 {
              padding: 1rem;
            }
            .rounded-lg {
              border-radius: 0.5rem;
            }
            .grid {
              display: grid;
            }
            .grid-cols-1 {
              grid-template-columns: repeat(1, minmax(0, 1fr));
            }
            .text-sm {
              font-size: 0.875rem;
            }
            .pre-wrap {
              white-space: pre-wrap;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
            }
            th {
              background-color: #f2f2f2;
              text-align: left;
            }
          </style>
        </head>
        <body>
          <div>
            <div class="flex justify-between items-center mb-4">
              <div class="text-right text-sm">
                <a href="mailto:support@radsforvets.com" class="text-blue-600 hover:underline">
                  support@radsforvets.com
                </a>
                <div>1-888-303-RADS (7237)</div>
              </div>
            </div>
            <div class="text-center text-2xl font-bold my-4 flex items-center justify-center gap-4">
              <span class="border-t-2 border-gray-300 w-24 text-center"></span>
              ${t("finalReport")}
              <span class="border-t-2 border-gray-300 w-24"></span>
            </div>
            <div class="bg-blue-50 grid grid-cols-1 gap-4 rounded-lg text-sm pre-wrap">
              ${processedHeader}
            </div>
          </div>
          <div class="body-content">
            ${getPrintContentFromReport({
              editor: editorRef.current?.editor,
              transcript,
            })}
          </div>
          <div class="footer">
            ${processedFooter}
          </div>
          <script>
            window.onafterprint = function() {
              window.close();
            };
            
            setTimeout(function() {
              window.print();
            }, 1000);
          </script>
        </body>
      </html>
    `;

    try {
      const printWindow = window.open(
        "",
        "_blank",
        "height=600,width=800,titlebar=yes,menubar=yes",
      );

      if (!printWindow) {
        alert(t("pleaseAllowPopups"));
        return;
      }

      printWindow.document.write(printModalContent);
      printWindow.document.close();
    } catch (error) {
      console.error("Error opening print window:", error);
      alert(t("errorOpeningPrintWindow"));
    }
  }, [transcript, processedHeader, processedFooter, t]);

  const handleCopy = useCallback(() => {
    const payload = getReportClipboardPayload({
      editor: editorRef.current?.editor,
      transcript,
      rteRef,
    });
    if (!payload.plainText.trim() && !payload.html.trim()) return;
    void writeReportToClipboard(payload)
      .then(() => {
        toast.success(t("contentCopiedToClipboard"));
      })
      .catch((err) => {
        console.error("Failed to copy content: ", err);
        toast.error(t("failedToCopyContent"));
      });
  }, [t, transcript, rteRef]);

  const handleTemplateSelect = useCallback(
    (template) => {
      setSelectedTemplateId(template?.templateId ?? null);

      const processedContent = processText(template.content);
      setTranscript(processedContent);
      if (isCreAuth) {
        // New template's saved instructions + manual text + Resident block
        // (predefined text + pasted resident report); only the previous
        // template's saved instructions are replaced.
        const incoming = template?.templateInstructions || "";
        const previous = lastTemplateInstructionsRef.current;
        setTemplateInstructions((current) =>
          resolveTemplateInstructionsOnSelect(current, previous, incoming),
        );
        lastTemplateInstructionsRef.current = incoming;
      } else {
        setTemplateInstructions("");
      }
    },
    [processText, setTranscript, isCreAuth],
  );

  const handleTemplateAppend = useCallback(
    (template) => {
      const editor = editorRef.current?.editor;
      if (!editor) {
        toast.warning(t("editorNotReady"));
        return;
      }

      const processedContent = processText(template?.content);
      if (!String(processedContent ?? "").trim()) {
        toast.error(t("appendTemplateEmptyText"));
        return;
      }

      const result = insertHtmlIntoReportEditor({
        editor,
        html: processedContent,
        setTranscript,
        onError: () => {
          toast.error(
            t("mdt.insertFailed") || "Failed to insert content into report.",
          );
        },
      });

      if (!result.ok) {
        if (result.reason === "empty") {
          toast.error(t("appendTemplateEmptyText"));
        }
        return;
      }

      toast.success(
        t("templateAppended", {
          templateName: template?.templateName ?? "",
        }),
      );
    },
    [processText, setTranscript, t],
  );

  handleTemplateSelectRef.current = handleTemplateSelect;

  // Single source of truth for anatomy selection:
  // whenever we have a selected template id and the templates list,
  // set anatomy from the template (works for both manual selection and draft restore).
  useEffect(() => {
    // If template is cleared, anatomy should not stay "stuck" from a previous template.
    // Same rule as Process AI uses directly (resolveTemplateAnathomy).
    const resolved = resolveTemplateAnathomy({
      templateId: selectedTemplateId,
      templates: templatesForVoice,
      anathomies,
    });
    if (resolved === ANATHOMY_UNKNOWN) return; // template list not loaded yet
    setSelectedAnathomy(resolved);
  }, [selectedTemplateId, templatesForVoice, anathomies]);

  const handleMacroSelect = useCallback(
    (macro) => {
      const macroBody = macro?.content ?? macro?.Content ?? "";

      // If the editor ref exists, insert the macro text at the current cursor position
      if (editorRef.current?.editor) {
        // Store cursor position before inserting
        // const editor = editorRef.current.editor;

        try {
          // Insert the macro text at the current cursor position
          // The macro is already filtered by species, modality, study type, and user in the API
          addRecordedTranscript(macroBody);

          // Optional: Show a success notification
          toast.success(t("macroInserted", { macroName: macro.macroName }));
        } catch (error) {
          console.error("Error inserting macro:", error);
          toast.error(t("failedToInsertMacro"));
        }
      } else {
        console.warn("Editor not available for macro insertion");
        toast.warning(t("editorNotReady"));
      }
    },
    [addRecordedTranscript, t],
  );

  const handleSave = useCallback(async () => {
    if (isSavingReport) return;
    setIsSavingReport(true);
    try {
      const reportObj = {
        StudyId: studyId,
        RelatedUserId: relatedUserId,
        ReportContent: getSaveContentFromReport({
          editor: editorRef.current?.editor,
          transcript,
        }),
      };

      if (!isCreAuth && findingsOnly) {
        reportObj.Restrictions = { FindingsOnly: true };
      }

      await createReports(reportObj);
    } catch (error) {
      console.error("Error creating report:", error);
      toast.error(t("failedToSaveReport"));
    } finally {
      setIsSavingReport(false);
      hideModal();
    }
  }, [
    studyId,
    relatedUserId,
    transcript,
    hideModal,
    t,
    isCreAuth,
    findingsOnly,
    isSavingReport,
  ]);

  const handleReset = useCallback(async () => {
    hideModal();
    try {
      if (isMicActive) {
        stopMic();
      }
      if (sidebarStopMicRef.current) {
        await sidebarStopMicRef.current();
      }
    } catch (e) {
      console.warn("Could not stop active mic before reset:", e);
    }

    resetTranscriptState();
    setSelectedTemplateId(null);
    setTemplateInstructions("");
    lastTemplateInstructionsRef.current = "";
    setIsAiLoading(false);
    removeDraft();
    toast.info(t("contentReset"));
  }, [hideModal, isMicActive, stopMic, resetTranscriptState, removeDraft, t]);

  const handleModalAction = useCallback(() => {
    if (actionType === "reset") {
      handleReset();
    } else if (actionType === "clearTranscription") {
      const performClear = pendingClearTranscriptionRef.current;
      pendingClearTranscriptionRef.current = null;
      hideModal();
      performClear?.();
    }
  }, [actionType, handleReset, hideModal]);

  const handleModalClose = useCallback(() => {
    pendingClearTranscriptionRef.current = null;
    hideModal();
  }, [hideModal]);

  // ──────────────────────────────────────────────────────────────────────
  // Hotkey handlers
  // ──────────────────────────────────────────────────────────────────────
  // F3 — toggle the editor (report engine) mic in DICTATION mode.
  // `handleEditorMicClick` already (a) forces command mode OFF,
  // (b) stops the sidebar mic, and (c) toggles the editor mic.
  const toggleEditorDictation = useCallback(async () => {
    await handleEditorMicClick();
  }, [handleEditorMicClick]);

  // Shift+F3 — toggle command mode for the editor mic. (Shift, not Ctrl,
  // because macOS reserves Ctrl+F3 for "Move focus to the Dock".)
  // If we're turning command mode ON and the editor mic isn't already
  // running, start it (after stopping the sidebar mic) — but we do NOT
  // call `handleEditorMicClick` here because that resets command mode.
  const toggleEditorCommandMode = useCallback(async () => {
    const nextCommandMode = !isCommandMode;
    if (nextCommandMode && !isMicActive) {
      if (!requirePermission(CRE_PERMISSION.BASIC)) {
        return;
      }
      setIsCommandMode(true);
      if (sidebarStopMicRef.current) {
        await sidebarStopMicRef.current();
      }
      await handleMicClick();
      return;
    }
    setIsCommandMode(nextCommandMode);
  }, [
    isCommandMode,
    isMicActive,
    handleMicClick,
    requirePermission,
  ]);

  // F2 — toggle sidebar Findings mic (always starts in the dictation box).
  const toggleSidebarMic = useCallback(async () => {
    await handleSidebarMicClick();
  }, [handleSidebarMicClick]);

  const handleReportResetRequest = useCallback(() => {
    showModal(t("radicalDeleteReportConfirmation"), "reset");
  }, [showModal, t]);

  const handleDeleteTranscriptionRequest = useCallback(
    (performClear) => {
      pendingClearTranscriptionRef.current = performClear;
      showModal(
        t("radicalDeleteTranscriptionConfirmation"),
        "clearTranscription",
      );
    },
    [showModal, t],
  );

  // CRE Agent Mode: connection to the Windows agent (inactive everywhere else).
  // Every command reuses the CRE functions above, so behaviour is CRE's.
  const agentNewPatient = useCallback(async () => {
    await handleReset();
    setSidebarTranscript("");
  }, [handleReset]);
  const agentOrganisations = useMemo(
    () =>
      agentMode
        ? getCreOrganisations(creUser).map((o) => ({
            organisationId: String(o.organisationId),
            name: getCreOrganisationLabel(o),
          }))
        : [],
    [agentMode, creUser],
  );
  useCreAgentBridge({
    enabled: agentMode,
    templates: templatesForVoice,
    selectedTemplateId,
    selectedAnathomyCode: anathomy,
    noTemplateMode: isCreAuth ? noTemplateMode : false,
    sidebarTranscript,
    setSidebarTranscript,
    selectTemplate: handleTemplateSelect,
    processWithAI: handleProcessWithAI,
    isAiLoading,
    getReport: () =>
      getReportClipboardPayload({
        editor: editorRef.current?.editor,
        transcript,
        rteRef,
      }),
    newPatient: agentNewPatient,
    reportLanguage: language,
    setReportLanguage: changeLanguage,
    organisations: agentOrganisations,
    selectedOrganisationId: creSelectedOrganisationId,
    setOrganisation: (id) =>
      useGlobalStore.getState().setCreSelectedOrganisation(id),
    // Agent Mode only: lists edited in the agent's "Templates & macros" window.
    reloadCatalog: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["template"] }),
        queryClient.invalidateQueries({ queryKey: ["macro"] }),
      ]),
  });

  // R4V save-on-save: parent posts RADICAL_SAVE_REPORT → save directly (no confirmation modal).
  useRadicalSaveReportMessage(handleSave);
  // R4V host: peer review requested/cancelled → bold first line of the Impression.
  useRadicalPeerReviewMessage(editorRef, transcript);

  const philipsSpeechMikeOptions = {
    isMicActive,
    isCommandMode,
    toggleEditorDictation,
    toggleEditorCommandMode,
    toggleSidebarMic,
    stopSidebarMic,
    selectNextParagraph,
    selectPreviousParagraph,
    selectContentUnderHeader,
    handleProcessWithAI,
    isCreAuth,
    handleSave,
  };

  usePhilipsSpeechMikeCommands(philipsSpeechMikeOptions);
  usePhilipsDeviceConnector(philipsSpeechMikeOptions);

  const renderDynamicComponent = useCallback(() => {
    switch (selectedComponent) {
      case "Study Details":
        return <StudyDetails />;
      case "Attached Documents":
        return (
          <AttachedDocument
            studyId={studyId}
            onInsertIntoReport={handleInsertIntoReport}
            language={language}
          />
        );
      case "Critical Findings":
        return <CriticalFinding />;
      case "Wet Reads":
        return <WetReads study={firstStudyData} />;
      case "Chats":
        return (
          <ChatErrorBoundary>
            <Chat
              chatConnection={chatConnection}
              studyId={studyId}
              language={language}
              studyData={studyData}
            />
          </ChatErrorBoundary>
        );
      case "PACSShare":
        return (
          <ChatErrorBoundary>
            <PacsShareTab
              chatConnection={chatConnection}
              studyId={studyId}
              language={language}
              studyData={studyData}
              organisationId={creSelectedOrganisationId}
            />
          </ChatErrorBoundary>
        );
      case "Protocol":
        return <ProtocolPanel language={language} />;
      case "ACR":
        return <AcrDecisionSupport language={language} />;
      case "Video Recording":
        return <VideoRecording />;
      case "MDT Recording":
        return (
          <MDTRecording
            studyId={studyId}
            relatedUserId={relatedUserId}
            language={language}
            onInsertIntoReport={handleInsertIntoReport}
          />
        );
      default:
        return null;
    }
  }, [
    selectedComponent,
    firstStudyData,
    studyId,
    chatConnection,
    language,
    studyData,
    relatedUserId,
    handleInsertIntoReport,
    creSelectedOrganisationId,
  ]);

  return (
    <div
      className={`dark:bg-gray-900 text-gray-900 dark:text-gray-200 ${
        isRadicalAuth || agentMode ? "p-1" : "p-2"
      }`}
    >
      {/* Agent Mode: no CRE chrome — compact/expand is the agent's own control. */}
      {!agentMode && (
        <>
          <Navbar
            setSidebarTranscript={setSidebarTranscript}
            language={language}
            setLanguage={changeLanguage}
          />
          <Menubar
            onSelect={handleSelectNewSection}
            showPreview={showPreview}
            transcript={transcript}
            editorRef={editorRef}
            language={language}
            chatUnreadCount={chatConnection.unreadCount}
          />
        </>
      )}

      {selectedComponent && !agentMode ? (
        <div key={selectedComponent}>{renderDynamicComponent()}</div>
      ) : null}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={orderedSections}
          strategy={horizontalListSortingStrategy}
        >
          <div
            className={
              agentMode
                ? "flex flex-col gap-2 px-1"
                : `flex flex-col lg:flex-row gap-2 px-1 sm:px-2 ${
                    isRadicalAuth ? "mt-1" : "mt-6"
                  }`
            }
          >
            {orderedSections.map((sectionId) => {
              if (sectionId === "rte") {
                return (
                  <ReportEditorSection
                    key={sectionId}
                    id={sectionId}
                    transcript={transcript}
                    loading={isAiLoading}
                    listeners={{}}
                    rteRef={rteRef}
                    editorRef={editorRef}
                    onTranscriptChange={handleTranscriptChange}
                    onProcessWithAI={handleProcessWithAI}
                    onCopy={handleCopy}
                    onPrint={handlePrint}
                    onPreview={showPreview}
                    isEditorMicActive={isMicActive}
                    onMicClick={handleEditorMicClick}
                    onEditorFocus={handleEditorFocus}
                    isCommandMode={isCommandMode}
                    streamingTranscript={streamingTranscript}
                    templateInstructions={templateInstructions}
                    setTemplateInstructions={setTemplateInstructions}
                    language={language}
                    dictationLanguage={dictationLanguage}
                    modelSpeed={modelSpeed}
                    setModelSpeed={setModelSpeed}
                    findingsOnly={findingsOnly}
                    onFindingsOnlyChange={setFindingsOnly}
                    onReset={handleReportResetRequest}
                    noTemplateMode={isCreAuth ? noTemplateMode : false}
                    onNoTemplateModeChange={
                      isCreAuth ? setNoTemplateMode : undefined
                    }
                    formulaQueueCount={isCreAuth ? formulaQueue.length : 0}
                    onFormulaQueue={isCreAuth ? enqueueFormula : undefined}
                    formulaManualDialog={
                      isCreAuth ? formulaManualDialog : null
                    }
                    onFormulaManualOpen={
                      isCreAuth
                        ? (formula, extras) =>
                            openFormulaManualDialog(formula, extras || {})
                        : undefined
                    }
                    onFormulaManualClose={
                      isCreAuth ? closeFormulaManualDialog : undefined
                    }
                    selectedTemplateName={
                      noTemplateMode &&
                      isCreAuth &&
                      isHebrewReportLanguage(language)
                        ? t("noTemplateMode", {
                            defaultValue: "General Report / No Template",
                          })
                        : templatesForVoice.find(
                            (tpl) =>
                              tpl &&
                              String(tpl.templateId) ===
                                String(selectedTemplateId),
                          )?.templateName ?? ""
                    }
                  />
                );
              } else if (sectionId === "sidebar") {
                // Agent Mode: dictation happens in the agent's own window, so the
                // CRE sidebar is not shown — but stays mounted (templates, macros,
                // dictation state and Process AI inputs keep working as in CRE).
                const sidebar = (
                  <SidebarSection
                    key={sectionId}
                    id={sectionId}
                    language={language}
                    onTemplateSelect={handleTemplateSelect}
                    onTemplateAppend={handleTemplateAppend}
                    onMacroSelect={handleMacroSelect}
                    onBeforeSidebarMicStart={handleBeforeSidebarMicStart}
                    registerSidebarStopMicHandler={
                      registerSidebarStopMicHandler
                    }
                    registerSidebarStartMicHandler={
                      registerSidebarStartMicHandler
                    }
                    registerSidebarClearReportHandler={
                      registerSidebarClearReportHandler
                    }
                    onDeleteTranscriptionRequest={
                      handleDeleteTranscriptionRequest
                    }
                    findingsTextareaRef={sidebarTextareaRef}
                    micToggleTarget={micToggleTarget}
                    onSidebarFocus={handleSidebarFocus}
                    onSidebarRecordingChange={setIsSidebarRecording}
                    onSidebarMicClick={handleSidebarMicClick}
                    isAnyMicActive={isAnyMicActive}
                    onMacrosReady={setMacrosForVoice}
                    onTemplatesReady={setTemplatesForVoice}
                    selectedTemplateId={selectedTemplateId}
                    onSelectedTemplateIdChange={setSelectedTemplateId}
                    relatedUserId={relatedUserId}
                    sidebarTranscript={sidebarTranscript}
                    setSidebarTranscript={setSidebarTranscript}
                    anathomy={anathomy}
                    dictationLanguage={dictationLanguage}
                    setDictationLanguage={setDictationLanguage}
                    anathomies={anathomies}
                    onProcessWithAI={handleProcessWithAI}
                    isAiLoading={isAiLoading}
                    onVoiceMacroResult={handleVoiceMacroResult}
                    modelSpeed={modelSpeed}
                    setModelSpeed={setModelSpeed}
                    findingsOnly={findingsOnly}
                    onFindingsOnlyChange={setFindingsOnly}
                  />
                );
                return agentMode ? (
                  <div key={sectionId} className="hidden" aria-hidden="true">
                    {sidebar}
                  </div>
                ) : (
                  sidebar
                );
              }
              return null;
            })}
          </div>
        </SortableContext>
      </DndContext>
      <Modal
        isVisible={isModalVisible}
        message={modalMessage}
        onClose={handleModalClose}
        onClick={handleModalAction}
        layout="radicalDeleteReport"
        cancelLabel={
          actionType === "clearTranscription"
            ? t("radicalDeleteTranscriptionCancel")
            : t("radicalDeleteReportCancel")
        }
        confirmLabel={
          actionType === "clearTranscription"
            ? t("radicalDeleteTranscriptionConfirm")
            : t("radicalDeleteReportConfirm")
        }
      />
      <PreviewModal
        isVisible={isPreviewVisible}
        onClose={hidePreview}
        studyData={studyData}
        editorRef={editorRef}
        transcript={transcript}
      />
      {!agentMode && <Footer />}
    </div>
  );
}

export default Patient;
